import type { RiskProfile, RiskSpec } from '@/types/schema/AccountRisk';
import type {
  TradingSetupRiskSnapshot,
  SessionStrategyOverride,
  SessionPlaybookOverride,
  RiskDerivationLevel,
  RiskDerivationStage,
} from '@/types/schema/TradingSetupSnapshots';
import type { StrategyPlaybookPair } from '@/types/shared/TradingSetupTypes';
import type { AccountRiskService } from '@/services/AccountRiskService';
import { isEntityActive } from '@/types/shared/EntityStatus';

/** Minimal service contract for profile lookup — enables lightweight stubs in tests. */
type RiskConfigReader = Pick<AccountRiskService, 'getActiveByAccountId'>;

/**
 * Resolves the effective risk for a single (strategyId, playbookId) pair
 * using the AccountRisk cascade: playbookOverrides > strategyOverrides > accountDefault.
 *
 * Pure function — does not mutate inputs.
 */
export function computeEffectiveRisk(
  strategyId: string,
  playbookId: string,
  profile: RiskProfile
): RiskSpec {
  const playbookMatch = profile.playbookOverrides?.find(
    (o) => o.playbookId === playbookId
  );
  if (playbookMatch) return { type: playbookMatch.risk.type, value: playbookMatch.risk.value };

  const strategyMatch = profile.strategyOverrides?.find(
    (o) => o.strategyId === strategyId
  );
  if (strategyMatch) return { type: strategyMatch.risk.type, value: strategyMatch.risk.value };

  return { type: profile.accountDefault.type, value: profile.accountDefault.value };
}

/**
 * Resolves the strategy-level risk from the AccountRisk cascade,
 * skipping the playbook override step:
 * strategyOverrides > accountDefault.
 *
 * Used to display the effective risk at the strategy header level
 * on the S&P card before session overrides are layered on.
 *
 * Pure function — does not mutate inputs.
 */
export function computeStrategyRisk(
  strategyId: string,
  profile: RiskProfile
): RiskSpec {
  const strategyMatch = profile.strategyOverrides?.find(
    (o) => o.strategyId === strategyId
  );
  if (strategyMatch) return { type: strategyMatch.risk.type, value: strategyMatch.risk.value };

  return { type: profile.accountDefault.type, value: profile.accountDefault.value };
}

/** Outcome returned by the internal cascade resolver. */
interface SessionAwareRiskOutcome {
  risk: RiskSpec;
  level: RiskDerivationLevel;
  stage: RiskDerivationStage;
}

/**
 * Single canonical cascade implementation.
 *
 * Priority (most specific wins):
 *   sessionPlaybookOverride > sessionStrategyOverride >
 *   profilePlaybookOverride > profileStrategyOverride > profileAccountDefault
 *
 * Returns the winning risk value together with derivation metadata.
 * Both public wrappers delegate here to prevent cascade drift.
 *
 * @internal Not exported — callers use computeSessionAwareRisk or determineDerivationSource.
 */
function resolveSessionAwareRiskOutcome(
  strategyId: string,
  playbookId: string,
  profile: RiskProfile,
  sessionOverrides?: {
    strategyOverrides?: SessionStrategyOverride[];
    playbookOverrides?: SessionPlaybookOverride[];
  }
): SessionAwareRiskOutcome {
  // Session playbook override (most specific)
  const sessionPlaybookMatch = sessionOverrides?.playbookOverrides?.find(
    (o) => o.strategyId === strategyId && o.playbookId === playbookId
  );
  if (sessionPlaybookMatch) {
    return {
      risk: { type: sessionPlaybookMatch.risk.type, value: sessionPlaybookMatch.risk.value },
      level: 'playbook-override',
      stage: 'session',
    };
  }

  // Session strategy override
  const sessionStrategyMatch = sessionOverrides?.strategyOverrides?.find(
    (o) => o.strategyId === strategyId
  );
  if (sessionStrategyMatch) {
    return {
      risk: { type: sessionStrategyMatch.risk.type, value: sessionStrategyMatch.risk.value },
      level: 'strategy-override',
      stage: 'session',
    };
  }

  // Profile playbook override
  const profilePlaybookMatch = profile.playbookOverrides?.find(
    (o) => o.playbookId === playbookId
  );
  if (profilePlaybookMatch) {
    return {
      risk: { type: profilePlaybookMatch.risk.type, value: profilePlaybookMatch.risk.value },
      level: 'playbook-override',
      stage: 'profile',
    };
  }

  // Profile strategy override
  const profileStrategyMatch = profile.strategyOverrides?.find(
    (o) => o.strategyId === strategyId
  );
  if (profileStrategyMatch) {
    return {
      risk: { type: profileStrategyMatch.risk.type, value: profileStrategyMatch.risk.value },
      level: 'strategy-override',
      stage: 'profile',
    };
  }

  // Account default (lowest specificity)
  return {
    risk: { type: profile.accountDefault.type, value: profile.accountDefault.value },
    level: 'account-default',
    stage: 'profile',
  };
}

/**
 * Resolves effective risk with session overrides layered on top of
 * the profile cascade. Full priority (most specific wins):
 *
 *   sessionPlaybookOverride > sessionStrategyOverride >
 *   profilePlaybookOverride > profileStrategyOverride > profileAccountDefault
 *
 * Pure function — does not mutate inputs.
 */
export function computeSessionAwareRisk(
  strategyId: string,
  playbookId: string,
  profile: RiskProfile,
  sessionOverrides?: {
    strategyOverrides?: SessionStrategyOverride[];
    playbookOverrides?: SessionPlaybookOverride[];
  }
): RiskSpec {
  return resolveSessionAwareRiskOutcome(strategyId, playbookId, profile, sessionOverrides).risk;
}

/**
 * Determines which cascade level and stage produced the effective risk
 * for a (strategy, playbook) pair with optional session overrides.
 *
 * Same cascade logic as computeSessionAwareRisk — both delegate to the
 * same internal resolver to prevent drift.
 *
 * Used at confirmation time to persist derivation metadata alongside
 * the computed effective risk in the TradingSetupRiskSnapshot.
 *
 * Pure function — does not mutate inputs.
 */
export function determineDerivationSource(
  strategyId: string,
  playbookId: string,
  profile: RiskProfile,
  sessionOverrides?: {
    strategyOverrides?: SessionStrategyOverride[];
    playbookOverrides?: SessionPlaybookOverride[];
  }
): { level: RiskDerivationLevel; stage: RiskDerivationStage } {
  const outcome = resolveSessionAwareRiskOutcome(strategyId, playbookId, profile, sessionOverrides);
  return { level: outcome.level, stage: outcome.stage };
}

/**
 * Computes effective risk for an array of strategy-playbook pairs
 * against a single RiskProfile. Preserves input order.
 *
 * Pure function — does not mutate inputs.
 */
export function computeEffectiveRiskForPairs(
  pairs: StrategyPlaybookPair[],
  profile: RiskProfile
): Array<StrategyPlaybookPair & { effectiveRisk: RiskSpec }> {
  return pairs.map((pair) => ({
    strategyId: pair.strategyId,
    playbookId: pair.playbookId,
    effectiveRisk: computeEffectiveRisk(pair.strategyId, pair.playbookId, profile),
  }));
}

/**
 * Retrieves the default RiskProfile for a given account.
 *
 * Resolution: oldest active AccountRisk config → defaultProfileId (if active) →
 * first active profile in array order → undefined.
 *
 * Read-only — no side effects, no auto-creation.
 */
export async function getDefaultRiskProfile(
  accountId: string,
  service: RiskConfigReader
): Promise<RiskProfile | undefined> {
  const configs = await service.getActiveByAccountId(accountId);
  if (configs.length === 0) return undefined;

  const sorted = [...configs].sort((a, b) =>
    a.createdAt.localeCompare(b.createdAt)
  );
  const canonical = sorted[0];
  if (!canonical) return undefined;

  const activeProfiles = canonical.riskProfiles.filter((p) =>
    isEntityActive(p.status)
  );
  if (activeProfiles.length === 0) return undefined;

  if (canonical.defaultProfileId) {
    const defaultProfile = activeProfiles.find(
      (p) => p.id === canonical.defaultProfileId
    );
    if (defaultProfile) return defaultProfile;
  }

  return activeProfiles[0];
}

/**
 * Assembles a TradingSetupRiskSnapshot from computed pairs and metadata.
 *
 * Deep-clones pairs and optional fields to prevent post-construction
 * mutation of the snapshot. Sets confirmedAt to the current ISO timestamp.
 */
export function buildTradingSetupRiskSnapshot(params: {
  defaultAccountId: string;
  pairs: Array<StrategyPlaybookPair & { effectiveRisk: RiskSpec; derivedFromLevel?: RiskDerivationLevel; derivedFromStage?: RiskDerivationStage }>;
  selectedRiskProfileId: string;
  selectedRiskProfileName: string;
  sessionStrategyOverrides?: SessionStrategyOverride[];
  sessionPlaybookOverrides?: SessionPlaybookOverride[];
}): TradingSetupRiskSnapshot {
  return {
    defaultAccountId: params.defaultAccountId,
    pairs: params.pairs.map((p) => ({
      strategyId: p.strategyId,
      playbookId: p.playbookId,
      effectiveRisk: { type: p.effectiveRisk.type, value: p.effectiveRisk.value },
      ...(p.derivedFromLevel ? { derivedFromLevel: p.derivedFromLevel } : {}),
      ...(p.derivedFromStage ? { derivedFromStage: p.derivedFromStage } : {}),
    })),
    selectedRiskProfileId: params.selectedRiskProfileId,
    selectedRiskProfileName: params.selectedRiskProfileName,
    ...(params.sessionStrategyOverrides && params.sessionStrategyOverrides.length > 0
      ? { sessionStrategyOverrides: params.sessionStrategyOverrides.map((s) => ({
          strategyId: s.strategyId,
          risk: { type: s.risk.type, value: s.risk.value },
          ...(s.deviationNoteId !== undefined ? { deviationNoteId: s.deviationNoteId } : {}),
        })) }
      : {}),
    ...(params.sessionPlaybookOverrides && params.sessionPlaybookOverrides.length > 0
      ? { sessionPlaybookOverrides: params.sessionPlaybookOverrides.map((s) => ({
          strategyId: s.strategyId,
          playbookId: s.playbookId,
          risk: { type: s.risk.type, value: s.risk.value },
          ...(s.deviationNoteId !== undefined ? { deviationNoteId: s.deviationNoteId } : {}),
        })) }
      : {}),
    confirmedAt: new Date().toISOString(),
  };
}
